import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { Profile } from '../types';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

interface TypingUser {
  id: string;
  displayName: string;
  expiresAt: number;
}

export function useTyping(conversationId: string | null, profile: Profile | null) {
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastSentAtRef = useRef(0);

  useEffect(() => {
    if (!conversationId || !profile || !isSupabaseConfigured) {
      setTypingUsers([]);
      return;
    }

    const client = getSupabase();
    const channel = client.channel(`typing:${conversationId}`, {
      config: {
        broadcast: {
          self: false,
        },
      },
    });

    channel
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        const userId = payload.user_id as string;
        if (!userId || userId === profile.id) return;

        setTypingUsers((current) => {
          const next = current.filter((user) => user.id !== userId && user.expiresAt > Date.now());
          next.push({
            id: userId,
            displayName: (payload.display_name as string) || 'Someone',
            expiresAt: Date.now() + 3000,
          });
          return next;
        });
      })
      .subscribe();

    channelRef.current = channel;
    const interval = window.setInterval(() => {
      setTypingUsers((current) => current.filter((user) => user.expiresAt > Date.now()));
    }, 1000);

    return () => {
      window.clearInterval(interval);
      channelRef.current = null;
      client.removeChannel(channel);
    };
  }, [conversationId, profile]);

  const sendTyping = useCallback(() => {
    const channel = channelRef.current;
    if (!channel || !profile) return;

    const now = Date.now();
    if (now - lastSentAtRef.current < 1200) return;
    lastSentAtRef.current = now;

    channel.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        user_id: profile.id,
        display_name: profile.display_name,
      },
    });
  }, [profile]);

  return { typingUsers, sendTyping };
}

